import { Grid } from '@material-ui/core';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { setHomePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { getDefaultWeightUomText } from 'src/common/helpers/localized-description-helper';
import { IDepartment } from 'src/common/interfaces/dtos/IDepartment';
import { IItemInputData } from 'src/common/interfaces/IItemInputData';
import { POSModeState } from 'src/common/interfaces/states/POSModeState';
import { AppState, store } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import Keypad from 'src/keypad/components/keypad';
import SalesKeypad, { ISalesKeypadModifiers } from 'src/sales-keypad/components/sales-keypad';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import DepartmentSaleApproval from './department-sale-approval';
import DepartmentSaleList from './department-sale-list';
import { departmentSaleStyles } from './department-sale-styles';
import DepartmentSaleTitle from './department-sale-title';
import { ILocalizedDescription } from 'src/common/interfaces/dtos/ILocalizedDescription';
import { useTranslation } from 'react-i18next';
import { UomText } from 'src/common/enumerations/unit-of-measure-type';
import { IButton } from 'src/common/interfaces/IButton';

export const showDepartmentSalePage = (props: IDepartmentSaleState): void => {
  setHomePage(<DepartmentSale {...props} />, undefined, { enableSearch: true });
};

enum MainScreen {
  DEPARTMENTS,
  APPROVAL,
}

const DepartmentSale: React.FC<IDepartmentSaleState> = (
  props: IDepartmentSaleState,
): JSX.Element => {
  const { t } = useTranslation();
  const classes = departmentSaleStyles();
  const trainingModeClasses = trainingModeStyles();
  const weightUOM = getDefaultWeightUomText();
  const isLeftHanded = useSelector((state: AppState) => state.cashierPreferences.leftHanded);
  const { isManualWeight, metricValue, type } = useSelector((state: AppState) => state.salesKeypad);

  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [mainScreen, setMainScreen] = useState<MainScreen>(MainScreen.DEPARTMENTS);
  const [itemPrice, setItemPrice] = useState<string>('');
  const posModeState: POSModeState = store.getState().posMode;
  const { inputData, onAccept } = props;

  const handleDepartmentSelected = useCallback(
    (departmentId: string) => {
      setSelectedDepartmentId(departmentId);
      setMainScreen(MainScreen.APPROVAL);
    },
    [setSelectedDepartmentId, setMainScreen],
  );

  const handleApprovalCancel = useCallback(() => {
    setMainScreen(MainScreen.DEPARTMENTS);
    setSelectedDepartmentId('');
  }, [setSelectedDepartmentId, setMainScreen]);

  const handleApproval = useCallback(
    (itemPrice: string): void => {
      if (itemPrice && itemPrice !== '.') {
        const newInputData: IItemInputData = {
          ...inputData,
          itemId: selectedDepartmentId,
          entryMethod: EntryMethod.Tapped,
          quantity: metricValue,
          isManualWeight: isManualWeight,
        };
        onAccept(newInputData, Number(itemPrice));
      } else {
        popupMessage({ message: 'DepartmentSaleView_PriceEnteredNotValid' });
      }
    },
    [selectedDepartmentId, inputData, onAccept, isManualWeight, metricValue],
  );

  const onKeypadSubmit = useCallback(
    (data: string): void => {
      switch (mainScreen) {
        case MainScreen.DEPARTMENTS:
          if (!props.departments.find(d => d.departmentId === data)) {
            popupMessage({ message: `Department does not exist: ${data}` });
            break;
          }
          setSelectedDepartmentId(data);
          setMainScreen(MainScreen.APPROVAL);
          break;
        case MainScreen.APPROVAL:
          handleApproval(itemPrice);
          break;
      }
    },
    [
      setSelectedDepartmentId,
      setMainScreen,
      handleApproval,
      mainScreen,
      props.departments,
      itemPrice,
    ],
  );

  const keypadSubmitButton: IButton = {
    label: 'OK',
    onClick: onKeypadSubmit,
  };

  const inputFieldText = isManualWeight
    ? `${metricValue} ${UomText[type]}`
    : `${UomText[type]}  ${metricValue}`;

  const emptyDescription: Partial<ILocalizedDescription> = {};
  const emptyIDepartment: IDepartment = {
    departmentId: '',
    descriptions: [emptyDescription],
  };

  return (
    <Grid
      item
      className={`fullHeight flexFull`}
      style={{ width: '0', marginLeft: '1vh' }}
      data-testid='department-sale'
    >
      <Grid item className={`${classes.background} fullWidth fullHeight`}>
        <Grid
          container
          direction='column'
          style={{ flex: '1 1 0' }}
          className={`fullHeight ${classes.departmentSaleContainer} ${
            posModeState.trainingMode ? trainingModeClasses.trainingModeBackground : ''
          }`}
          data-testid='department-sale-container'
        >
          <Grid
            item
            container
            className={
              posModeState.trainingMode
                ? trainingModeClasses.trainingModeBackground
                : classes.background
            }
          >
            <DepartmentSaleTitle
              title={t(
                posModeState.returnMode.returnType
                  ? 'ReturnByDepartment'
                  : 'HeaderControl_DepartmentSale',
              )}
              itemWeight={
                props.inputData.isManualWeight ? `${props.inputData.quantity} ${weightUOM}` : ''
              }
            />
          </Grid>
          <Grid item container style={{ flex: 1 }}>
            {isLeftHanded && (
              <DepartmentSaleKeypad
                onKeypadSubmit={onKeypadSubmit}
                mainScreen={mainScreen}
                inputFieldText={inputFieldText}
                keypadSubmitButton={keypadSubmitButton}
              />
            )}
            {mainScreen === MainScreen.DEPARTMENTS ? (
              <DepartmentSaleList
                departments={props.departments}
                onDepartmentSelected={handleDepartmentSelected}
                onCancel={props.onCancel}
              />
            ) : (
              <DepartmentSaleApproval
                department={
                  props.departments.find(d => d.departmentId === selectedDepartmentId) ??
                  emptyIDepartment
                }
                handleApprove={handleApproval}
                handleCancel={handleApprovalCancel}
                isManualWeight={!!props.inputData.isManualWeight}
                itemPrice={itemPrice}
                setItemPrice={setItemPrice}
              />
            )}
            {!isLeftHanded && (
              <DepartmentSaleKeypad
                onKeypadSubmit={onKeypadSubmit}
                mainScreen={mainScreen}
                inputFieldText={inputFieldText}
                keypadSubmitButton={keypadSubmitButton}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const DepartmentSaleKeypad: React.FC<DepartmentSaleKeypadProps> = ({
  onKeypadSubmit,
  mainScreen,
  inputFieldText,
  keypadSubmitButton,
}): JSX.Element => {
  const classes = departmentSaleStyles();
  const { t } = useTranslation();
  const KEYPAD_SELECT_DEPARTMENT_TEXT = t('DepartmentSaleKeypadHeader');
  const KEYPAD_ENTER_APPROVAL_PRICE_TEXT = t('DepartmentSaleApprovalPriceText');
  const KEYPAD_HINT = t('DepartmentSaleKeypadHintText');
  return (
    <Grid item className={classes.keypadContainer}>
      {mainScreen === MainScreen.APPROVAL && (
        <Keypad
          inputFieldText={inputFieldText}
          keyboardId='department-sale'
          submitButton={keypadSubmitButton}
          visible={true}
          topLabelText={KEYPAD_ENTER_APPROVAL_PRICE_TEXT}
          inputInstructionsLabelText={KEYPAD_HINT}
          keypadTestIdPrefix='department-sale-'
          noInputField
          disableManualWeight
          disableQuantity
          replaceDecimalInput={true}
        />
      )}
      {mainScreen === MainScreen.DEPARTMENTS && (
        <SalesKeypad
          dataTestIdModifier={'department-sale'}
          keyboardId='department-sale'
          isKeypadVisible={true}
          keyPadSubmit={onKeypadSubmit}
          topLabelText={
            mainScreen === MainScreen.DEPARTMENTS
              ? KEYPAD_SELECT_DEPARTMENT_TEXT
              : KEYPAD_ENTER_APPROVAL_PRICE_TEXT
          }
          disableDecimalInput
          disableManualWeight
          inputInstructionsLabelText={KEYPAD_HINT}
        />
      )}
    </Grid>
  );
};

export default DepartmentSale;

export interface IDepartmentSaleState {
  departments: IDepartment[];
  inputData: IItemInputData;
  onAccept: (inputData: IItemInputData, newPrice: number) => void;
  onCancel: () => void;
}

interface DepartmentSaleKeypadProps {
  onKeypadSubmit: (input: string, salesKeypadModifiers: ISalesKeypadModifiers) => void;
  mainScreen: MainScreen;
  inputFieldText: string;
  keypadSubmitButton: IButton;
}
